/**
 * Live view frame pump.
 *
 * The console used to ask the Function App for a frame URL on every tick and
 * then let an <img> fetch it. That put two Azure round trips — a user
 * delegation key and a blob property read — in front of every single frame, so
 * polling faster than about 3 Hz cost more than it showed, and the picture
 * moved in visible steps.
 *
 * The SAS the backend hands out is valid for two minutes, and the device
 * overwrites one fixed blob. So the URL is a *subscription*, not a
 * per-frame ticket: mint it rarely, then read the blob directly as fast as the
 * device publishes. The Function App leaves the per-frame path entirely.
 *
 * Reading the blob with fetch() instead of an <img> also fixes something that
 * was only ever nearly true. The overlay used to be read by a second Azure
 * call, so the box could describe a different frame than the one on screen.
 * Now the image and its overlay come out of one HTTP response — the device
 * writes both in a single blob write, and blob metadata rides back as
 * `x-ms-meta-overlay` on the same GET.
 */

/** Decode the device's overlay from the blob metadata header. */
function parseOverlay(headerValue) {
  if (!headerValue) return null;
  try {
    // base64 JSON: the device encodes it so labels with non-ASCII characters
    // survive as blob metadata, which must be ASCII.
    const json = decodeURIComponent(
      atob(headerValue)
        .split("")
        .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
        .join(""),
    );
    return JSON.parse(json);
  } catch (_e) {
    return null;
  }
}

/**
 * Fetch one camera's newest frame.
 *
 * Returns `null` when there is nothing new — either the blob has not changed
 * (304 against the stored ETag) or the frame that came back is older than the
 * one already on screen. That second case is not hypothetical: the device
 * uploads through a small pool of workers, so two frames can be in flight at
 * once and land out of order. A viewer must never be walked backwards.
 */
export async function fetchFrame(source, { signal } = {}) {
  const headers = source.etag ? { "If-None-Match": source.etag } : {};
  // Cache-bust: the blob is written with `no-store`, but a proxy or a browser
  // heuristic that ignores that would freeze the view on one frame — the exact
  // symptom this code exists to remove.
  const res = await fetch(`${source.url}&_=${Date.now()}`, {
    headers,
    cache: "no-store",
    signal,
  });
  if (res.status === 304) return null;
  if (!res.ok) {
    const err = new Error(`frame fetch failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  const overlay = parseOverlay(res.headers.get("x-ms-meta-overlay"));
  const ts = overlay?.ts ?? 0;
  if (ts && source.lastTs && ts <= source.lastTs) return null;
  const blob = await res.blob();
  if (!blob.size) return null;
  return {
    objectUrl: URL.createObjectURL(blob),
    overlay,
    ts,
    etag: res.headers.get("ETag") || null,
  };
}

/**
 * How long to wait before asking for the next frame.
 *
 * Paced from the *end* of the previous fetch rather than on a fixed interval,
 * so a slow link stretches the cadence instead of stacking up requests that
 * all deliver stale frames at once — which is what a setInterval pump does the
 * moment the network is slower than its period, and it looks like stuttering.
 */
export function nextDelay(targetIntervalMs, elapsedMs) {
  return Math.max(0, targetIntervalMs - elapsedMs);
}
